import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { IndividualProjectTemplate as template } from './individual-project.template';
import { IndividualProjectStyles as styles } from './individual-project.styles';
import { IProjectModalService, IProjectService, IVppaService } from '../../services';
import { IProjectDocumentStatus, IVppa } from '../../types';
import { logger, PROJECT_OPERATIONAL_STATUS } from '../../utils';
import { VppaModal } from '../../components/modals/vppa-modal/vppa-modal';
import { CancelModal } from '../../components/modals/cancel-modal/cancel-modal';
import { IStructuredChatService } from '../../services/structured-chat.service';
import { IndividualProjectsDrilldown } from '../../components/page-specific/individual-project-components/individual-projects-drilldown/individual-projects-drilldown';
import { IndividualProjectsOverview } from '../../components/page-specific/individual-project-components/individual-projects-overview/individual-projects-overview';
import { notifyError } from '../../utils/notify';
import {IUserService} from "../../services/user.service";
import {ProjectModal} from "../../components/page-specific/project-modal/project-modal";
import { Message, SocketSubscription } from '@genesislcap/foundation-comms';
import {MoreInfoModal} from "../../components/common/more-info/more-info-modal/more-info-modal";
const name = 'individual-project';

@customElement({
  name,
  template,
  styles,
})
export class IndividualProject extends FASTElement {
  @IUserService userService: IUserService;
  @IProjectModalService projectModalService: IProjectModalService;
  @IProjectService projectService: IProjectService;
  @IStructuredChatService structuredChatService: IStructuredChatService;
  @IVppaService vppaService: IVppaService;
  @observable project: IProjectDocumentStatus;
  @observable isNewBuild: boolean = false;
  @observable projectModal: ProjectModal;

  public projectDrilldown: IndividualProjectsDrilldown;
  public projectOverview: IndividualProjectsOverview;
  public vppaModal: VppaModal;
  public cancelModal: CancelModal;

  private projectSubscription: SocketSubscription;
  public moreInfoModal: MoreInfoModal;

  connectedCallback() {
    super.connectedCallback();
    const pathArray = window.location.pathname.split('/');
    const projectId = pathArray[pathArray.length - 1];
    this.streamProject(projectId);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.projectSubscription?.unsubscribe();
  }

  private streamProject(projectId) {
    this.projectSubscription = this.projectService.getProjectDocumentStatus(projectId).subscribe({
      next: (streamMessage: Message) => {
        if (streamMessage.ROW) {
          let result = streamMessage.ROW[0] ?? null;
          if (result && result.DETAILS) {
            const type = result.DETAILS.OPERATION;

            if (type === 'INSERT') {
              this.project = result;
              this.projectDrilldown.setProject(this.project);
              this.projectOverview.setProject(this.project);
            }

            if (type === 'MODIFY') {
              this.project = { ...this.project, ...result };
              this.projectDrilldown.updateProject(this.project);
              this.projectOverview.updateProject(this.project);
            }
            this.isNewBuild =
              this.project.PROJECT_OPERATIONAL_STATUS === PROJECT_OPERATIONAL_STATUS.NEW_BUILD;
          }
        }
      },
      error: (error: any) => {
        this.project = null;
        logger.error(error);
      },
    });
  }

  public createProposal(detail) {
    this.structuredChatService.openStructuredChat({
      selectedVPPAIDs: [detail.VPPA_ID],
      selectedRFQIDs: [],
      selectedNegotiationID: '',
    });
  }
  public editVppa(detail) {
    this.vppaModal.openModal(true, detail.VPPA_ID, null, detail.TICKER);
  }

  public copyVppa(detail) {
    this.vppaModal.openModal(false, detail.VPPA_ID, null, detail.TICKER);
  }

  public openVppaModal() {
    this.vppaModal.openModal(false, '', this.project.PROJECT_ID);
  }

  public deleteVppa(detail) {
    this.cancelModal.openModal(
      detail,
      'VPPA',
      'Are you sure you wish to cancel ' + detail.TICKER + '?',
    );
  }

  public downloadVppa(detail) {
    this.vppaService.generateFile(detail.VPPA_ID).then(async (response) => {
      const xlsxName = detail.NAME + ' ' + detail.UNIQUE_ID + '.xlsx';
      if (!response.ok) {
        console.error(response);
      } else {
        const data = await response.blob();
        this.vppaService.fileDownload(data, xlsxName);
      }
    });
  }

  public cancelVppa(data: IVppa) {
    this.vppaService.eventVppaCancel(data.VPPA_ID, data.CONTRACT_TYPE).then(
      (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
          this.cancelModal.closeDialog();
        } else {
          notifyError(response.ERROR[0].TEXT);
          logger.error(response.ERROR);
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      },
    );
  }

  public openModal() {
    this.projectModal.openModal();
  }

  public editProject() {
    this.projectModalService.modalMode = 'edit';
    this.openModal();
    this.projectModal.populateAllForms(this.project);
  }

  public openMoreInfoModal(data) {
    this.moreInfoModal.openMoreInfoModal(data);
  }
}
